import React, { forwardRef } from 'react';
import { css } from '@emotion/react';
import AspectRatio from '@polestar/component-warehouse-react/components/aspect-ratio/AspectRatio';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Heading from '@polestar/component-warehouse-react/components/heading/Heading';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import styled from '@emotion/styled';
import AspectRatios from '@polestar/component-warehouse-react/tokens/aspect-ratios/AspectRatios';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import { createCSSTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { TextAnchorButton } from '@polestar/component-warehouse-react/components/button/Button';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const Card = forwardRef(({ orientation = Orientations.vertical, media, mediaAspectRatio, mediaOverlay, title, body, actions, disableMobileOptimization = false, ...props }, ref) => {
    var _a;
    const memoizedId = useSSRSafeId('card', props.id);
    const isLink = ((_a = props === null || props === void 0 ? void 0 : props.as) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'a' || false;
    return (React.createElement(StyledCard, { ...props, isLink: isLink, ref: ref },
        React.createElement(Wrapper, { orientation: orientation, disableMobileOptimization: disableMobileOptimization },
            media && (React.createElement(CardMedia, { orientation: orientation, mediaAspectRatio: mediaAspectRatio, disableMobileOptimization: disableMobileOptimization, mediaOverlay: mediaOverlay }, media)),
            (title || body || actions) && (React.createElement(StyledColumn, null,
                title && React.createElement(CardTitle, { id: `${memoizedId}-title` }, title),
                title && body && React.createElement(Spacer, { spacing: Spacings.medium }),
                body && React.createElement(CardBody, null, body),
                actions && (React.createElement("div", null,
                    React.createElement(Spacer, { orientation: Orientations.vertical, spacing: Spacings.medium }),
                    React.createElement(CardAction, { ...aria({
                            describedby: title && `${memoizedId}-title`,
                        }), isLink: isLink }, actions))))))));
});
Card.displayName = 'Card';
export default Card;
const LinkCardContainer = Card;
export const LinkCard = forwardRef((props, ref) => (React.createElement(LinkCardContainer, { ref: ref, as: "a", ...props })));
LinkCard.displayName = 'LinkCard';
const Wrapper = styled('div', shouldNotForwardProps(true, 'orientation'))(({ orientation, disableMobileOptimization }) => css `
    display: flex;

    ${responsiveStyles({
    flexDirection: {
        mobile: disableMobileOptimization
            ? orientation === Orientations.vertical
                ? 'column'
                : 'row'
            : 'column',
        tablet: orientation === Orientations.vertical ? 'column' : 'row',
    },
})}
  `);
const CardMedia = ({ mediaAspectRatio, orientation, children, disableMobileOptimization, mediaOverlay, }) => {
    let ratio;
    if (mediaAspectRatio !== undefined) {
        ratio = mediaAspectRatio;
    }
    else {
        ratio = orientation === Orientations.horizontal ? AspectRatios.square : AspectRatios.xWide;
    }
    return (React.createElement(StyledAspectRatio, { orientation: orientation, disableMobileOptimization: disableMobileOptimization },
        React.createElement(AspectRatio, { ratio: ratio }, children),
        !!mediaOverlay && React.createElement(MediaOverlay, { "data-type": "cardOverlay" }, mediaOverlay)));
};
const StyledColumn = styled(Column) `
  flex: 1;
`;
const CardTitle = ({ children, ...props }) => typeof children === 'string' ? (React.createElement(StyledTitle, { ...props, as: "h2", lineClamp: 2 }, children)) : (React.createElement(React.Fragment, null, children));
const CardBody = ({ children }) => typeof children === 'string' ? (React.createElement(StyledParagraph, { lineClamp: 3 }, children)) : (React.createElement(React.Fragment, null, children));
const CardAction = ({ children, isLink, ...props }) => typeof children === 'string' ? (React.createElement(StyledTextButton, { as: isLink ? 'span' : undefined, href: "#", ...props }, children)) : (React.createElement(React.Fragment, null, children));
const StyledAspectRatio = styled('div', shouldNotForwardProps(true, 'orientation'))((props) => {
    const { enabled } = props.theme.card.states;
    return css(css `
      position: relative;
      flex-shrink: 0;
      overflow: hidden;

      img {
        transition: ${createCSSTransition('transform', enabled.motionEase, enabled.motionDuration)};
      }
    `, responsiveStyles({
        marginInlineEnd: props.orientation === Orientations.horizontal
            ? {
                mobile: props.disableMobileOptimization ? Grids.mobile.gutter : 0,
                tablet: Grids.tablet.gutter,
                desktop: Grids.desktop.gutter,
            }
            : {
                mobile: 0,
            },
        marginBlockEnd: {
            mobile: props.disableMobileOptimization
                ? props.orientation === Orientations.vertical
                    ? Spacings.medium
                    : 0
                : Spacings.medium,
            tablet: props.orientation === Orientations.vertical ? Spacings.medium : 0,
        },
        width: {
            mobile: props.disableMobileOptimization
                ? props.orientation === Orientations.vertical
                    ? '100%'
                    : `33.33%`
                : '100%',
            tablet: props.orientation === Orientations.vertical ? '100%' : `33.33%`,
        },
    }));
});
const StyledCard = styled.div(({ theme, isLink, ...props }) => {
    const { hover } = theme.card.states;
    return css(css `
      height: 100%;
      -webkit-tap-highlight-color: transparent;
    `, isLink &&
        css `
        text-decoration: none;

        &:hover {
          text-decoration: none;
        }
      `, ((props === null || props === void 0 ? void 0 : props.onClick) || isLink) &&
        css `
        cursor: pointer;

        &:hover {
          /* ensure that textbutton icon colors are not overwritten */

          [data-type='cardOverlay']:not,
          button div + span > svg {
            color: ${theme.card.states.hover.buttonIconColor};
          }
        }

        &:hover {
          [data-type='cardOverlay']:not,
          h2,
          p,
          a,
          div,
          button {
            color: ${hover.color};
            transition: ${createCSSTransition('color', hover.motionEase, hover.motionDuration)};
          }

          img {
            transform: scale(1.07);
            transition: ${createCSSTransition('transform', hover.motionEase, hover.motionDuration)};
          }
        }
      `);
});
const StyledTitle = styled(Heading)(({ theme }) => css `
    transition: ${createCSSTransition('color', theme.card.states.enabled.motionEase, theme.card.states.enabled.motionDuration)};
  `);
const StyledParagraph = styled(Paragraph)(({ theme }) => css `
    transition: ${createCSSTransition('color', theme.card.states.enabled.motionEase, theme.card.states.enabled.motionDuration)};
  `);
const StyledTextButton = styled(TextAnchorButton)(({ theme }) => css `
    transition: ${createCSSTransition('color', theme.card.states.enabled.motionEase, theme.card.states.enabled.motionDuration)};
  `);
const MediaOverlay = styled.div `
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
`;
